import { useEffect, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { useTranslate } from 'shared/lib/i18n'

/**
 * «Регистрация возврата» — support registers a buyer's return for a partner without
 * an API (ADR-0037, spec §18; ticket 42): 139 Express has no API and no file for
 * returns. The return's code, the order, the Point, the buyer (and the document, for a
 * partner identifying by document), an optional return code, and support's comment
 * — as the partner told it. A partner with an API registers its returns itself, so it
 * is not offered. A refusal is the server's, in words. The form's body — the page
 * opens it in a dialog and closes it on `onRegistered`.
 *
 * Every call is injected so the behaviour is tested without a backend.
 */
export type RegisterPartner = RouterOutputs['buyerReturns']['Partners'][number]
export type RegisterPoint = RouterOutputs['buyerReturns']['Points'][number]
export type RegisterRequest = RouterInputs['buyerReturns']['Register']
export type RegisterAnswer = RouterOutputs['buyerReturns']['Register']

export interface RegisterReturnProps {
	listPartners?: () => Promise<RegisterPartner[]>
	listPoints?: () => Promise<RegisterPoint[]>
	register?: (input: RegisterRequest) => Promise<RegisterAnswer>
	/** Called with what to tell support once the return is registered (or was already). */
	onRegistered?: (message: string) => void
	/** Closes the form without registering. */
	onCancel?: () => void
}

const partnersViaTrpc = () => trpcClient.buyerReturns.Partners.query()
const pointsViaTrpc = () => trpcClient.buyerReturns.Points.query()
const registerViaTrpc = (input: RegisterRequest) => trpcClient.buyerReturns.Register.mutate(input)

const SELECT_CLASS =
	'h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'

const EMPTY = {
	trackingCode: '',
	partnerOrderRef: '',
	recipientName: '',
	clientRef: '',
	document: '',
	returnCode: '',
	comment: '',
}

export function RegisterReturn({
	listPartners = partnersViaTrpc,
	listPoints = pointsViaTrpc,
	register = registerViaTrpc,
	onRegistered,
	onCancel,
}: RegisterReturnProps) {
	const { t } = useTranslate()
	const [partners, setPartners] = useState<RegisterPartner[]>([])
	const [points, setPoints] = useState<RegisterPoint[]>([])
	const [partnerId, setPartnerId] = useState('')
	const [pointId, setPointId] = useState('')
	const [form, setForm] = useState(EMPTY)
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

	useEffect(() => {
		listPartners().then((list) => setPartners(list.filter((p) => !p.byApi)), () => setPartners([]))
		listPoints().then(setPoints, () => setPoints([]))
	}, [listPartners, listPoints])

	const partner = partners.find((p) => p.partnerId === partnerId)
	const needsDocument = partner?.documentType != null
	const filled =
		partnerId !== '' &&
		pointId !== '' &&
		form.trackingCode.trim() !== '' &&
		form.partnerOrderRef.trim() !== '' &&
		form.recipientName.trim() !== '' &&
		form.clientRef.trim() !== '' &&
		form.comment.trim() !== '' &&
		(!needsDocument || form.document.trim() !== '')

	const field = (key: keyof typeof EMPTY, labelKey: string) => (
		<div className='grid gap-1.5'>
			<Label htmlFor={`register-return-${key}`}>{t(labelKey as never)}</Label>
			<Input
				id={`register-return-${key}`}
				data-testid={`register-return-${key}`}
				value={form[key]}
				onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
			/>
		</div>
	)

	const submit = async () => {
		if (!filled || busy) return
		setBusy(true)
		setMessage(null)
		try {
			const answer = await register({
				partnerId,
				pointId,
				trackingCode: form.trackingCode.trim(),
				partnerOrderRef: form.partnerOrderRef.trim(),
				recipientName: form.recipientName.trim(),
				clientRef: form.clientRef.trim(),
				...(needsDocument ? { document: form.document.trim() } : {}),
				...(form.returnCode.trim() ? { returnCode: form.returnCode.trim() } : {}),
				comment: form.comment.trim(),
			})
			if (answer.outcome === 'refused') {
				setMessage({ ok: false, text: t(`returnRefusal.${answer.reason}`) })
			} else {
				const text = t(answer.outcome === 'registered' ? 'registerReturn.done' : 'registerReturn.already', {
					code: form.trackingCode.trim(),
				})
				setForm(EMPTY)
				// Told where the form sits (the page closes its dialog and says it there), or here.
				if (onRegistered) onRegistered(text)
				else setMessage({ ok: true, text })
			}
		} catch (e) {
			setMessage({ ok: false, text: e instanceof Error ? e.message : String(e) })
		} finally {
			setBusy(false)
		}
	}

	return (
		<form
			className='grid gap-4'
			data-testid='register-return'
			onSubmit={(e) => {
				e.preventDefault()
				void submit()
			}}
		>
			<div className='grid gap-3 sm:grid-cols-2'>
				<div className='grid gap-1.5'>
					<Label htmlFor='register-return-partner'>{t('registerReturn.partner')}</Label>
					<select
						id='register-return-partner'
						data-testid='register-return-partner'
						className={SELECT_CLASS}
						value={partnerId}
						onChange={(e) => setPartnerId(e.target.value)}
					>
						<option value=''>{t('registerReturn.choose')}</option>
						{partners.map((p) => (
							<option key={p.partnerId} value={p.partnerId}>
								{p.name}
							</option>
						))}
					</select>
				</div>
				<div className='grid gap-1.5'>
					<Label htmlFor='register-return-point'>{t('registerReturn.point')}</Label>
					<select
						id='register-return-point'
						data-testid='register-return-point'
						className={SELECT_CLASS}
						value={pointId}
						onChange={(e) => setPointId(e.target.value)}
					>
						<option value=''>{t('registerReturn.choose')}</option>
						{points.map((p) => (
							<option key={p.pointId} value={p.pointId}>
								{p.id}
							</option>
						))}
					</select>
				</div>
				{field('trackingCode', 'registerReturn.trackingCode')}
				{field('partnerOrderRef', 'registerReturn.order')}
				{field('recipientName', 'registerReturn.buyer')}
				{field('clientRef', 'registerReturn.clientRef')}
				{needsDocument && field('document', 'registerReturn.document')}
				{field('returnCode', 'registerReturn.returnCode')}
			</div>
			<div className='grid gap-1.5'>
				<Label htmlFor='register-return-comment'>{t('registerReturn.comment')}</Label>
				<Textarea
					id='register-return-comment'
					data-testid='register-return-comment'
					value={form.comment}
					onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
				/>
			</div>
			{message && (
				<p
					role={message.ok ? 'status' : 'alert'}
					className={message.ok ? 'text-sm' : 'text-sm text-destructive'}
					data-testid='register-return-message'
				>
					{message.text}
				</p>
			)}
			<div className='flex justify-end gap-2'>
				{onCancel && (
					<Button type='button' variant='outline' onClick={onCancel} disabled={busy}>
						{t('registerReturn.cancel')}
					</Button>
				)}
				<Button type='submit' data-testid='register-return-submit' disabled={!filled || busy}>
					{t('registerReturn.submit')}
				</Button>
			</div>
		</form>
	)
}
